<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, computed } from 'vue'

const props = defineProps({
  dashboards: {
    type: Array,
    default: () => []
  },
  activeDashboard: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['select', 'create', 'update', 'delete', 'browse', 'import-dashboard'])

// Dropdown state
const isOpen = ref(false)
const triggerRef = ref(null)
const menuRef = ref(null)
const menuStyle = ref({})

// Interaction state
const hoveredId = ref(null)
const editingId = ref(null)
const editedName = ref('')
const deletingId = ref(null)
const creatingNew = ref(false)
const newName = ref('')

// Computed
const triggerText = computed(() => {
  return props.activeDashboard?.name || 'Select Dashboard'
})

// Position the dropdown menu
function updateMenuPosition() {
  if (!triggerRef.value || !isOpen.value) return

  const rect = triggerRef.value.getBoundingClientRect()
  const menuWidth = 320
  const menuHeight = menuRef.value?.offsetHeight || 400

  let left = rect.left
  let top = rect.bottom + 8

  // Check if dropdown would go off-screen bottom
  if (top + menuHeight > window.innerHeight - 10) {
    top = rect.top - menuHeight - 8
  }

  // Keep within viewport horizontally
  if (left < 10) left = 10
  if (left + menuWidth > window.innerWidth - 10) {
    left = window.innerWidth - menuWidth - 10
  }

  menuStyle.value = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    minWidth: `${menuWidth}px`,
    maxWidth: `${menuWidth}px`
  }
}

// Toggle dropdown
function toggle() {
  if (props.loading || props.disabled) return
  isOpen.value = !isOpen.value
}

// Close dropdown
function close() {
  isOpen.value = false
  hoveredId.value = null
  editingId.value = null
  deletingId.value = null
  creatingNew.value = false
  newName.value = ''
}

// Select a dashboard
function selectDashboard(dashboardId) {
  if (props.activeDashboard?.id === dashboardId) {
    close()
    return
  }
  emit('select', dashboardId)
  close()
}

// Edit functionality
function startEditing(dashboard) {
  editingId.value = dashboard.id
  editedName.value = dashboard.name
  deletingId.value = null
  nextTick(() => {
    const input = menuRef.value?.querySelector('.edit-input')
    input?.focus()
    input?.select()
  })
}

function saveEdit(dashboardId) {
  if (!editedName.value.trim()) return
  emit('update', dashboardId, editedName.value.trim())
  editingId.value = null
  editedName.value = ''
}

function cancelEdit() {
  editingId.value = null
  editedName.value = ''
}

// Delete functionality
function startDeleting(dashboardId) {
  deletingId.value = dashboardId
  editingId.value = null
}

function confirmDelete(dashboardId) {
  emit('delete', dashboardId)
  deletingId.value = null
  close()
}

function cancelDelete() {
  deletingId.value = null
}

// Create functionality
function startCreating() {
  creatingNew.value = true
  newName.value = ''
  nextTick(() => {
    const input = menuRef.value?.querySelector('.create-input')
    input?.focus()
  })
}

function createDashboard() {
  if (!newName.value.trim()) return
  emit('create', newName.value.trim())
  creatingNew.value = false
  newName.value = ''
  close()
}

function cancelCreate() {
  creatingNew.value = false
  newName.value = ''
}

// Watch for dropdown open/close
watch(isOpen, async (newVal) => {
  if (newVal) {
    await nextTick()
    updateMenuPosition()
  } else {
    // Reset states when closing
    hoveredId.value = null
    editingId.value = null
    deletingId.value = null
    creatingNew.value = false
    newName.value = ''
  }
})

// Close when clicking outside
function handleClickOutside(event) {
  if (triggerRef.value && triggerRef.value.contains(event.target)) return
  if (menuRef.value && menuRef.value.contains(event.target)) return
  close()
}

// Close on escape
function handleKeydown(event) {
  if (event.key === 'Escape') {
    if (editingId.value) {
      cancelEdit()
    } else if (deletingId.value) {
      cancelDelete()
    } else if (creatingNew.value) {
      cancelCreate()
    } else {
      close()
    }
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside, true)
  document.addEventListener('keydown', handleKeydown)
  window.addEventListener('resize', updateMenuPosition)
  window.addEventListener('scroll', updateMenuPosition, true)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside, true)
  document.removeEventListener('keydown', handleKeydown)
  window.removeEventListener('resize', updateMenuPosition)
  window.removeEventListener('scroll', updateMenuPosition, true)
})
</script>

<template>
  <div class="dashboard-selector-wrapper">
    <!-- Trigger Button -->
    <button
      ref="triggerRef"
      class="dropdown-trigger"
      :class="{ 'is-open': isOpen, 'is-loading': loading, 'is-enabled': !!activeDashboard }"
      :disabled="loading || disabled"
      @click="toggle"
    >
      <font-awesome-icon
        :icon="['fas', 'table-cells-large']"
        class="dropdown-trigger-icon"
      />
      <span class="dropdown-trigger-text">{{ triggerText }}</span>
      <font-awesome-icon
        :icon="['fas', 'chevron-down']"
        class="dropdown-trigger-chevron"
      />
    </button>

    <!-- Teleported Dropdown Menu -->
    <Teleport to="body">
      <Transition name="selector-dropdown">
        <div
          v-if="isOpen"
          ref="menuRef"
          class="selector-menu"
          :style="menuStyle"
          @click.stop
        >
          <!-- Dashboard List -->
          <div class="dashboards-list">
            <div
              v-for="dashboard in dashboards"
              :key="dashboard.id"
              class="dashboard-item"
              :class="{ 'is-active': dashboard.id === activeDashboard?.id }"
              @mouseenter="hoveredId = dashboard.id"
              @mouseleave="hoveredId = null"
            >
              <!-- Normal State -->
              <div
                v-if="editingId !== dashboard.id && deletingId !== dashboard.id"
                class="item-normal"
              >
                <span
                  class="item-name"
                  @click="selectDashboard(dashboard.id)"
                >
                  <font-awesome-icon
                    v-if="dashboard.relationship === 'subscribed'"
                    :icon="['fas', 'link']"
                    class="subscribed-icon"
                    title="Subscribed"
                  />
                  {{ dashboard.name }}
                </span>

                <!-- Subscribed: unsubscribe action on hover -->
                <div
                  v-if="dashboard.relationship === 'subscribed'"
                  class="item-actions"
                  :class="{ 'is-visible': hoveredId === dashboard.id }"
                  @click.stop
                >
                  <button
                    class="btn-icon-delete"
                    title="Unsubscribe"
                    @click="emit('delete', dashboard.id)"
                  >
                    <font-awesome-icon :icon="['fas', 'link-slash']" />
                  </button>
                </div>

                <!-- Owned: edit/delete actions on hover -->
                <div
                  v-else
                  class="item-actions"
                  :class="{ 'is-visible': hoveredId === dashboard.id }"
                  @click.stop
                >
                  <button
                    class="btn-icon-edit"
                    title="Edit name"
                    @click="startEditing(dashboard)"
                  >
                    <font-awesome-icon :icon="['fas', 'pen']" />
                  </button>
                  <button
                    class="btn-icon-delete"
                    title="Delete dashboard"
                    @click="startDeleting(dashboard.id)"
                  >
                    <font-awesome-icon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>

              <!-- Edit State -->
              <div
                v-if="editingId === dashboard.id"
                class="item-editing"
                @click.stop
              >
                <input
                  v-model="editedName"
                  type="text"
                  class="edit-input"
                  @keyup.enter="saveEdit(dashboard.id)"
                  @keyup.escape="cancelEdit"
                  @click.stop
                />
                <button
                  class="btn-confirm"
                  :disabled="!editedName.trim()"
                  @click="saveEdit(dashboard.id)"
                >
                  <font-awesome-icon :icon="['fas', 'check']" />
                </button>
                <button class="btn-cancel" @click="cancelEdit">
                  <font-awesome-icon :icon="['fas', 'times']" />
                </button>
              </div>

              <!-- Delete Confirmation State -->
              <div
                v-if="deletingId === dashboard.id"
                class="item-deleting"
                @click.stop
              >
                <span class="delete-text">Delete?</span>
                <div class="delete-actions">
                  <button class="btn-cancel" @click="cancelDelete">
                    <font-awesome-icon :icon="['fas', 'times']" />
                  </button>
                  <button
                    class="btn-confirm-delete"
                    @click="confirmDelete(dashboard.id)"
                  >
                    <font-awesome-icon :icon="['fas', 'check']" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Divider -->
          <div class="menu-divider"></div>

          <!-- Create New Dashboard -->
          <div class="create-section">
            <div
              v-if="!creatingNew"
              class="create-trigger"
              @click="startCreating"
            >
              <font-awesome-icon :icon="['fas', 'plus']" class="create-icon" />
              <span>Create New Dashboard</span>
            </div>

            <div v-else class="create-form" @click.stop>
              <input
                v-model="newName"
                type="text"
                placeholder="Dashboard name..."
                class="create-input"
                @keyup.enter="createDashboard"
                @keyup.escape="cancelCreate"
                @click.stop
              />
              <button
                class="btn-confirm"
                :disabled="!newName.trim()"
                @click="createDashboard"
              >
                <font-awesome-icon :icon="['fas', 'check']" />
              </button>
              <button class="btn-cancel" @click="cancelCreate">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
          </div>

          <!-- Divider -->
          <div class="menu-divider"></div>

          <!-- Browse & Import Section -->
          <div class="browse-section">
            <div class="create-trigger" @click="emit('browse'); close()">
              <font-awesome-icon :icon="['fas', 'compass']" class="create-icon" />
              <span>Browse Dashboards</span>
            </div>
            <div class="create-trigger" @click="emit('import-dashboard'); close()">
              <font-awesome-icon :icon="['fas', 'file-import']" class="create-icon" />
              <span>Import Dashboard</span>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.dashboard-selector-wrapper {
  position: relative;
  display: inline-flex;
}

/* Trigger styles (.dropdown-trigger, .dropdown-trigger-icon,
 * .dropdown-trigger-text, .dropdown-trigger-chevron) come from
 * @/styles/dropdown-trigger.css. The selector behaves like any other dropdown
 * trigger — muted by default, hover/open get the same purple/grey wash —
 * we just constrain its width so long names ellipsis instead of pushing
 * the rest of the toolbar around. */
.dropdown-trigger {
  min-width: 160px;
  max-width: 320px;
}

/* Dropdown Menu */
.selector-menu {
  background: var(--surface-page);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  overflow: hidden;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

/* Dashboard List */
.dashboards-list {
  overflow-y: auto;
  max-height: 400px;
}

.dashboards-list::-webkit-scrollbar {
  width: 6px;
}

.dashboards-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}

.dashboards-list::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: var(--radius-xs);
}

.dashboards-list::-webkit-scrollbar-thumb:hover {
  background: var(--border-strong);
}

/* Dashboard Item */
.dashboard-item {
  transition: background 0.15s ease;
}

.dashboard-item.is-active {
  background: var(--accent-soft);
}

/* Normal State */
.item-normal {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  min-height: 3rem;
  cursor: pointer;
}

.dashboard-item:not(.is-active) .item-normal:hover {
  background: var(--surface-overlay);
}

.dashboard-item.is-active .item-normal:hover {
  background: rgba(115, 87, 210, 0.12);
}

.item-name {
  flex: 1;
  font-size: 0.9375rem;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
  width: 4rem;
  justify-content: flex-end;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.item-actions.is-visible {
  opacity: 1;
  pointer-events: auto;
}

.btn-icon-edit,
.btn-icon-delete {
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  color: var(--text-faint);
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition-fast);
}

.btn-icon-edit:hover {
  background: var(--accent-soft-3);
  color: var(--accent);
}

.btn-icon-delete:hover {
  background: rgba(239, 68, 68, 0.15);
  color: var(--color-error);
}

/* Edit State */
.item-editing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
}

.edit-input {
  flex: 1;
  background: var(--surface-overlay-2);
  border: 1px solid var(--accent);
  border-radius: var(--radius-sm);
  padding: 0.375rem 0.625rem;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.edit-input:focus {
  outline: none;
  border-color: var(--accent);
}

/* Delete State */
.item-deleting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
}

.delete-text {
  flex: 1;
  font-size: 0.875rem;
  color: var(--color-error);
}

.delete-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-shrink: 0;
}

/* Confirm/Cancel Buttons */
.btn-confirm,
.btn-cancel {
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.btn-confirm {
  background: rgba(34, 197, 94, 0.2);
  color: var(--color-success);
}

.btn-confirm:hover:not(:disabled) {
  background: rgba(34, 197, 94, 0.3);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-cancel {
  background: rgba(239, 68, 68, 0.2);
  color: var(--color-error);
}

.btn-cancel:hover {
  background: rgba(239, 68, 68, 0.3);
}

.btn-confirm-delete {
  width: 1.75rem;
  height: 1.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.2);
  border: none;
  border-radius: var(--radius-sm);
  color: var(--color-error);
  font-size: 0.75rem;
  cursor: pointer;
  transition: var(--transition-fast);
  flex-shrink: 0;
}

.btn-confirm-delete:hover {
  background: rgba(239, 68, 68, 0.35);
}

/* Divider */
.menu-divider {
  height: 1px;
  background: var(--surface-overlay-3);
  margin: 0.25rem 0;
}

/* Create Section */
.create-section {
  padding: 0.25rem 0;
}

.create-trigger {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
  color: var(--text-faint);
  font-size: 0.8125rem;
  font-weight: 400;
}

.create-trigger:hover {
  background: var(--surface-overlay);
  color: var(--text-muted);
}

.create-icon {
  font-size: 0.75rem;
  width: 0.875rem;
  flex-shrink: 0;
}

.create-form {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
}

.create-input {
  flex: 1;
  background: var(--surface-overlay-2);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--radius-sm);
  padding: 0.375rem 0.625rem;
  color: var(--text-primary);
  font-size: 0.875rem;
}

.create-input:focus {
  outline: none;
  border-color: var(--accent);
}

.create-input::placeholder {
  color: var(--text-faint);
}

/* Subscribed icon */
.subscribed-icon {
  font-size: 0.625rem;
  color: var(--accent);
  margin-right: 0.375rem;
  opacity: 0.7;
}

/* Browse Section */
.browse-section {
  padding: 0.25rem 0;
}

/* Dropdown animation */
.selector-dropdown-enter-active,
.selector-dropdown-leave-active {
  transition: var(--transition-fast);
}

.selector-dropdown-enter-from,
.selector-dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
